import React, { useState, useEffect } from "react";
import {
    Box,
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    Menu,
    MenuItem,
    Avatar,
    Chip,
    Divider,
    Drawer,
    ListItemIcon,
    Badge
} from "@mui/material";
import {
    Menu as MenuIcon,
    Business,
    Settings,
    AccountCircle,
    Notifications,
    Logout
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import SidebarMenu from "../SidebarMenu";
import { getMenuConfig, getCustomColors } from "../../config/menuConfigs";

const AdminLayout = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout, isAdmin } = useAuth();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [customColors, setCustomColors] = useState(null);

    // Determinar tipo de admin basado en la ruta
    const adminType = location.pathname.startsWith("/admin/system") ? "system" : "business";

    // Cargar colores personalizados
    useEffect(() => {
        const savedColors = getCustomColors();
        if (savedColors) {
            setCustomColors(savedColors);
        }
    }, []);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleProfileMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleProfileMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = async () => {
        try {
            await logout();
            navigate("/login");
        } catch (error) {
            // Error handling silencioso para evitar logs innecesarios
        }
    };

    const handleColorsChange = (newColors) => {
        setCustomColors(newColors);
    };

    const getAdminTypeInfo = () => {
        return adminType === "business" 
            ? { title: "Admin de Negocio", color: "primary", icon: <Business /> }
            : { title: "Admin de Sistema", color: "secondary", icon: <Settings /> };
    };

    const adminInfo = getAdminTypeInfo();
    const menuConfig = getMenuConfig(adminType);

    const drawerWidth = 280;

    const drawer = (
        <Box>
            <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
                <Typography variant="h6" color="primary" sx={{ fontWeight: "bold" }}>
                    CAPIVIO Admin
                </Typography>
                <Chip 
                    label={adminInfo.title}
                    color={adminInfo.color}
                    icon={adminInfo.icon}
                    size="small"
                    sx={{ mt: 1 }}
                />
            </Box>
            
            {/* SidebarMenu unificado */}
            <SidebarMenu
                sections={menuConfig.sections}
                colors={customColors}
                title={menuConfig.title}
                showColorPicker={isAdmin}
                onColorsChange={handleColorsChange}
                defaultColors={menuConfig.defaultColors}
            />
        </Box>
    );

    return (
        <Box sx={{ display: "flex" }}>
            {/* AppBar */}
            <AppBar 
                position="fixed" 
                sx={{ 
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    backgroundColor: "white",
                    color: "primary.main",
                    boxShadow: 1
                }}
            >
                <Toolbar>
                    {/* Botón de menú para móvil */}
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { sm: "none" } }}
                    >
                        <MenuIcon />
                    </IconButton>

                    {/* Logo y título */}
                    <Box 
                        sx={{ 
                            flexGrow: 1, 
                            display: "flex", 
                            alignItems: "center",
                            cursor: "pointer"
                        }}
                        onClick={() => navigate(adminType === "business" ? "/admin/business" : "/admin/system")}
                    >
                        <Typography 
                            variant="h6" 
                            sx={{ 
                                fontWeight: "bold",
                                color: "primary.main"
                            }}
                        >
                            CAPIVIO {adminInfo.title}
                        </Typography>
                    </Box>

                    {/* Acciones del usuario */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {/* Notificaciones */}
                        <IconButton
                            size="large"
                            edge="end"
                            aria-label="notifications"
                            color="inherit"
                        >
                            <Badge badgeContent={4} color="error">
                                <Notifications />
                            </Badge>
                        </IconButton>
                        
                        {/* Menú de perfil */}
                        <IconButton
                            size="large"
                            edge="end"
                            aria-label="account of current user"
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            onClick={handleProfileMenuOpen}
                            color="inherit"
                        >
                            <Avatar sx={{ bgcolor: "primary.main", width: 32, height: 32 }}>
                                {user?.displayName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || "A"}
                            </Avatar>
                        </IconButton>
                        
                        <Menu
                            id="menu-appbar"
                            anchorEl={anchorEl}
                            anchorOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            open={Boolean(anchorEl)}
                            onClose={handleProfileMenuClose}
                        >
                            <MenuItem onClick={() => { navigate("/profile"); handleProfileMenuClose(); }}>
                                <ListItemIcon>
                                    <AccountCircle fontSize="small" />
                                </ListItemIcon>
                                Perfil
                            </MenuItem>
                            <Divider />
                            <MenuItem onClick={handleLogout}>
                                <ListItemIcon>
                                    <Logout fontSize="small" />
                                </ListItemIcon>
                                Cerrar Sesión
                            </MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>
            </AppBar>

            {/* Sidebar */}
            <Box
                component="nav"
                sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
            >
                {/* Sidebar para desktop */}
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: "none", sm: "block" },
                        "& .MuiDrawer-paper": { 
                            boxSizing: "border-box", 
                            width: drawerWidth,
                            border: "none"
                        }
                    }}
                    open
                >
                    <Toolbar /> {/* Espacio para AppBar */}
                    {drawer}
                </Drawer>

                {/* Sidebar para móvil */}
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true // Mejor rendimiento en móvil
                    }}
                    sx={{
                        display: { xs: "block", sm: "none" },
                        "& .MuiDrawer-paper": { 
                            boxSizing: "border-box", 
                            width: drawerWidth 
                        }
                    }}
                >
                    {drawer}
                </Drawer>
            </Box>

            {/* Contenido principal */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    minHeight: "100vh",
                    backgroundColor: "#f5f5f5"
                }}
            >
                <Toolbar /> {/* Espacio para AppBar */}
                {children}
            </Box>
        </Box>
    );
};

export default AdminLayout; 